<template>
  <div id="listOfAssociateEditors">
    <p class="mb-8">
      Select which <strong>two</strong> Associate Editors the manuscript should be submitted to.<br>
      You must select a first and second choice.
    </p>
    <NotificationComponent
      v-if="hasError"
      data-testid="failed-to-load-associate-editors"
      class="mb-8"
      role="alert"
      variant="error"
      heading-text="Failed to load Associate Editors"
      :heading-level="1">
      <template #body>
        <p>
          Unable to retrieve Associate Editor information. Please retry or <a
            data-testid="email-customer-service-get-associate-editors"
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
            id="linkCustomerServicesAssociateEditors"
            href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions:%20Unable%20to%20retrieve%20Associate%20Editor%20information">email customer services
          </a>
          if the issue persists.
        </p>
      </template>
    </NotificationComponent>

    <div v-if="showSelectedEditors">
      <div class="mt-12 mb-6">
        <h3 class="inline font-roboto text-xl font-bold">
          Selected
        </h3>
        <span
          ref="availableRef"
          :class="twoSelectedEditors"
          class="p-3 font-bold ml-2 rounded-full px-5">
          {{ selectedEditors.length }}/2
        </span>
      </div>

      <div class="border-2 border-royal-blue-20 rounded-lg p-2">
        <draggable
          v-model="selectedEditors"
          item-key="peerReviewIdentifier"
          tag="div"
          class="space-y-2"
          :disabled="selectedEditors.length <= 1"
          @end="() => reorderEditors(selectedEditors)"
        >
          <template #item="{ element: editor, index }">
            <div
              :class="[
                'p-4 bg-faint-grey rounded-lg',
                selectedEditors.length > 1 ? 'cursor-move' : 'cursor-default',
              ]"
              :data-testid="'at-least-two-associate-editors-required' + '-' + (index + 1)">
              <div class="flex flex-col md:flex-row gap-x-4 items-start">
                <div class="flex mb-4 md:mb-0 gap-x-2">
                  <div
                    v-if="selectedEditors.length > 1"
                    class="drag-handle shrink-0 cursor-move">
                    <DragHandleIcon
                      class="text-royal-blue-160"
                      :is-decorative="true"
                      title="Reorder"
                      :data-testid="`drag-handle-selected-editor-${index}`"
                    />
                  </div>
                  <div
                    :id="'selectedAssociateEditorNumberIcon' + '-' + (index + 1)"
                    class="w-6 h-6 flex items-center justify-center
                           border-2 border-royal-blue-160 bg-white
                           rounded-full text-base font-bold">
                    {{ index + 1 }}
                  </div>
                  <div class="w-30 h-40 ml-3 mr-2 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                    <img
                      v-if="shouldShowImage(editor)"
                      :src="getImageSrc(editor.image)"
                      :alt="editor.name"
                      class="object-cover w-full h-full"
                      @load="onImageLoad(editor)"
                      @error="onImageError(editor)"/>
                    <PersonIcon
                      v-else
                      class="w-full h-full text-gray-400" />
                  </div>
                </div>
                <div class="max-w-xl">
                  <h4
                    class="font-roboto text-lg font-bold mb-3"
                    :data-testid="`selected-editor-name-${index}`"
                  >
                    {{ editor.name }}
                  </h4>
                  <p class="font-bold mb-5">
                    {{ editor.area }}
                  </p>
                  <p class="mb-4">
                    {{ editor.affiliation }}
                  </p>
                </div>
                <div
                  class="md:ml-auto"
                  :data-testid="'selected-editor-remove' + '-' + (index + 1)">
                  <ButtonComponent
                    label="Remove"
                    variant="secondary"
                    @customClick="removeEditor(editor.peerReviewIdentifier)"
                  />
                </div>
              </div>
            </div>
          </template>
        </draggable>

        <div
          v-if="selectedEditors.length === 1"
          class="flex justify-center py-24">
          <p>
            Second Associate Editor required
          </p>
        </div>
      </div>
    </div>
    <h3 class="font-roboto text-xl font-bold mb-4 mt-8">
      Available
    </h3>
    <div
      tabindex="0"
      aria-label="Available Associate Editors"
      class="border-2 border-black rounded-lg p-2 max-h-128 overflow-y-auto
        [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      <EditorListItem
        v-for="(editor, index) in availableEditors"
        :key="editor.peerReviewIdentifier"
        :pos="index + 1"
        :editor="editor"
        :selected-editors="selectedEditors"
        @editorSelected="editor => selectEditor(editor, scrollToAvailable)"
        data-testid="associated-editor-selected" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import draggable from 'vuedraggable'
import EditorListItem from './EditorListItem.vue'
import { EditorialOffice } from '@/types'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import PersonIcon from '@/images/svg/PersonIcon.vue'
import DragHandleIcon from '../ui-components/icons/DragHandleIcon.vue'
import { useEditorialManagement } from '@/composables/useEditorialManagement'

const {
  selectedEditors,
  selectEditor,
  removeEditor,
  reorderEditors,
  allEditors,
  editorialOfficeFetchFailed,
} = useEditorialManagement()

const hasError = computed(() => editorialOfficeFetchFailed.value)
const availableRef = ref<HTMLElement | null>(null)

function scrollToAvailable() {
  nextTick(() => {
    availableRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

function getImageSrc(image: unknown): string {
  if (!image) return ''
  if (typeof image === 'string') return image
  if (image instanceof URL) return image.href
  return String(image)
}
const failedImageEditors = ref(new Set<string>())

function onImageLoad(editor: EditorialOffice) {
  failedImageEditors.value.delete(editor.peerReviewIdentifier)
}

function onImageError(editor: EditorialOffice) {
  failedImageEditors.value.add(editor.peerReviewIdentifier)
}

function shouldShowImage(editor: EditorialOffice): boolean {
  return !failedImageEditors.value.has(editor.peerReviewIdentifier)
}

const showSelectedEditors = computed(() => selectedEditors.value.length > 0)

const twoSelectedEditors = computed(() =>
  selectedEditors.value.length === 2 ? 'bg-[#D7F77D]' : 'bg-royal-blue-10',
)

const availableEditors = computed(() =>
  allEditors.value.filter(
    e =>
      e.enabled
      && selectedEditors.value.every(se => se.peerReviewIdentifier !== e.peerReviewIdentifier),
  ),
)

</script>
